import { B2Tabs } from "@hdb/biome-2";
import { useQuery } from "@tanstack/react-query";
import { CSSProperties, FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AssetDisplayLevelChartType } from "src/core/enums/dashboard-enums";
import TabularSelectComponent from "src/shared-elements/tabular-select/tabular-select";
import { generateFilenameWithTimestamp } from "src/shared/helpers/generateFilenameWithTimestamp";
import { getassetDisplayLevelAndLocationsByBlock } from "src/store/slices/dashboard.slice";
import { getBaselineListByModuleId } from "../../../../../core/api/services/baseline.service";
import { getBurnChartDataByBlockCodeWithLevelAndLocation } from "../../../../../core/api/services/dashbaorad-details/burn-time.service";
import CustomHighChartComponent from "../../../../../shared/custom-highchart-component/custom-highchart.component";
import CustomSpinnerComponent from "../../../../../shared/custom-spinner/custom-spinner.component";
import ChipComponent from "src/core/components/chip/chip.component";

interface BurnTimeChartTypes {
  blockCode: any;
  isRefreshed: boolean;
  blockDetails: any;
}

const BurnTimeChart: FC<BurnTimeChartTypes> = ({ blockCode, isRefreshed, blockDetails }) => {
  const dispatch = useDispatch();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [assetDisplayLevelsByBlock, setAssetDisplayLevelsByBlock] =
    useState<any>(null);
  const [activeKey, setActiveKey] = useState("Sensor");
  const [render, setRender] = useState(0);

  useEffect(() => {
    dispatch(
      // Old API for Levels
      //   getAssetDisplayLevelsByBlock({
      //     blockCode,
      //     chartType: AssetDisplayLevelChartType.BurnTime,
      //   })
      // ).then((response: any) => {
      //   setAssetDisplayLevelsByBlock(response?.payload);
      // });

      // New API for Levels and Locations
      getassetDisplayLevelAndLocationsByBlock({
        blockCode,
        chartType: AssetDisplayLevelChartType.BurnTime,
      })
    ).then((response: any) => {
      setAssetDisplayLevelsByBlock(response?.payload);
    });
  }, [blockCode]);

  // const assetDisplayLevelsByBlock = useSelector(
  //   (state: any) => state.dashboard.assetDisplayLevelsByBlock
  // );

  // Old API to fetch Burn Time Data
  // const { data: burnChartData, isLoading: isLoadingBurnChartData } = useQuery({
  //   queryKey: ["getBurnChartData", blockCode, selectedLocation],
  //   queryFn: ({ signal }) =>
  //     getBurnChartData(blockCode, selectedLocation, signal),
  //   enabled: !!blockCode || isRefreshed || !!selectedLocation,
  // });

  // New API to fetch Burn Time Data with Level and Location
  const { data: burnChartData, isLoading: isLoadingBurnChartData } = useQuery({
    queryKey: [
      "getBurnChartDataByBlockCodeWithLevelAndLocation",
      blockCode,
      selectedLocation,
    ],
    queryFn: ({ signal }) => {
      const locationType =
        assetDisplayLevelsByBlock?.find(
          (item: any) => item.DisplayLevel === selectedLocation
        )?.Type || null;
      return getBurnChartDataByBlockCodeWithLevelAndLocation(
        blockCode,
        selectedLocation,
        locationType,
        signal
      );
    },
    enabled: !!blockCode || isRefreshed || !!selectedLocation,
  });

  const [burnTimeData, setBurnTimeData] = useState<{
    burnTime: [];
    sensorNode: [];
    extraData: [];
  }>({
    burnTime: [],
    sensorNode: [],
    extraData: [],
  });

  const [burnTimeBaselineValues, setBurnTimeBaselineValues] = useState<any>({});

  const { data: burnTimeListByModule } = useQuery({
    queryKey: ["getBaselineListByModuleId", 1, blockCode],
    queryFn: () =>
      getBaselineListByModuleId({
        baselineModuleId: 1,
        blockCode: blockCode,
        assetCode: null,
      }),
  });

  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const burnTime = burnTimeListByModule?.data?.map((module: any) => {
      return module.value;
    });
    if (!burnTime) return;
    setEnabled(
      burnTime?.length ? burnTimeListByModule?.data[0].isEnabled : false
    );
    setBurnTimeBaselineValues({ base: burnTime, ranges: [] });
  }, [burnTimeListByModule]);

  useEffect(() => {
    if (burnChartData?.data?.length) {
      const filteredData = burnChartData?.data?.filter(
        (item: any) => item.burnTime != null
      );

      const burnTime = filteredData.map((d: any) => d.burnTime);

      const sensorNode = filteredData?.map((d: any) => d.assetDisplayName);
      const extraData = filteredData?.map((d: any) => {
        return {
          code: d?.assetCode,
          displayName: d?.assetDisplayName,
          burnTime: d?.burnTime,
          level: d?.level,
        };
      });

      setBurnTimeData({
        burnTime,
        sensorNode,
        extraData: extraData || [],
      });
    }
  }, [burnChartData]);

  const [yAxisMax, setYAxisMax] = useState<number>(50);
  const [yAxisMin, setYAxisMin] = useState<number>(0);

  useEffect(() => {
    const yAxisData = burnTimeData?.burnTime;
    const baselineData: any = burnTimeBaselineValues?.base;

    if (yAxisData?.length) {
      let minValue = 0;

      if (baselineData?.length) {
        minValue = Math.min(...yAxisData, ...baselineData) - 5;
      } else {
        minValue = Math.min(...yAxisData) - 5;
      }

      if (minValue < 0) {
        minValue = 0;
      }

      setYAxisMin(minValue);

      let maxValue = 50;
      if (baselineData?.length) {
        maxValue = Math.max(...yAxisData, ...baselineData) + 5;
      } else {
        maxValue = Math.max(...yAxisData) + 5;
      }

      setYAxisMax(maxValue);
    }
  }, [burnTimeData, burnTimeBaselineValues]);

  //

  const onChangeHandler = (selectedValue: any) => {
    const newValue = selectedValue === "All Level" ? null : selectedValue;

    // If there's no change, exit early
    if (newValue === selectedLocation) return;

    // Reset burn time data
    setBurnTimeData({
      burnTime: [],
      sensorNode: [],
      extraData: [],
    });

    // Update selection
    setSelectedLocation(newValue);
  };

  // Responsive placeholder for tabular filter
  const [responsivePlaceholder, setResponsivePlaceholder] = useState(
    window.innerWidth <= 992 ? "Select" : "Select Levels/Locations"
  );

  // Responsive style for tabular filter
  const [tabularFilterStyle, setTabularFilterStyle] = useState<
    CSSProperties | undefined
  >(undefined);

  // Update placeholder and style based on window width
  useEffect(() => {
    const updatePlaceholder = () => {
      if (window.innerWidth <= 992) {
        setResponsivePlaceholder("Select");
        setTabularFilterStyle({
          width: "15%",
          position: "absolute",
          zIndex: "999",
          top: "-50px",
          right: "0",
        });
      } else {
        setResponsivePlaceholder("Select Levels/Locations");
        setTabularFilterStyle({
          width: "22%",
          position: "absolute",
          zIndex: "999",
          top: "-50px",
          right: "0",
        });
      }
    };

    updatePlaceholder();
    window.addEventListener("resize", updatePlaceholder);

    return () => {
      window.removeEventListener("resize", updatePlaceholder);
    };
  }, []);

  const containerStyle: CSSProperties = {
    display: "flex",
    position: "relative",
  };

  // const selectStyle: CSSProperties = {
  //   width: "20%",
  //   position: "absolute",
  //   zIndex: "999",
  //   top: "-50px",
  //   right: "0",
  // };

  return (
    <div className="w-lg-48 w-100 mt-4">
      <div className="bg-card">
        <div className="d-md-flex">
          <div className="mb-4 mb-md-0">
            <h4 className="h4">
              Burn Time <span style={{ fontSize: "16px" }}>(Last Day){blockDetails?.slsSupplier == "ST Engineering" && <ChipComponent label="Estimated" />}</span>
            </h4>
          </div>
        </div>

        <div className="custom_tab">
          <B2Tabs
            key={render}
            activeKey={activeKey}
            pills
            tabClassName={""}
            onSelect={(selectedId: any) => {
              setActiveKey(selectedId);
            }}
            tab={[
              {
                content: (
                  <>
                    {activeKey === "Sensor" ? (
                      <>
                        <div className="w-100" style={containerStyle}>
                          <div style={tabularFilterStyle}>
                            <TabularSelectComponent
                              selectedValue={selectedLocation}
                              options={assetDisplayLevelsByBlock}
                              placeHolder={responsivePlaceholder}
                              onChange={onChangeHandler}
                            />
                          </div>
                        </div>
                        <CustomSpinnerComponent
                          height={"400px"}
                          width={"100%"}
                          isLoading={isLoadingBurnChartData || isRefreshed}
                        >
                          <CustomHighChartComponent
                            xAxisCategories={burnTimeData?.sensorNode}
                            SeriesType={"column"}
                            seriesData={burnTimeData?.burnTime}
                            seriesShowInLegend={false}
                            seriesName={"Burn Time(hours)"}
                            xAxisTitleText={"Sensors"}
                            yAxisTitleText={"Burn Time(hours)"}
                            // colors={["#2AAFD7"]}
                            colors={["#66b2ff"]}
                            baseLine={enabled ? burnTimeBaselineValues : {}}
                            extraData={burnTimeData?.extraData}
                            redirectRoute="/asset/"
                            // yAxisMin={yAxisMin}
                            // yAxisMax={yAxisMax}
                            fileName={generateFilenameWithTimestamp(
                              "Burn Time (Last day)"
                            )}
                            chartName={"Burn Time (Last day)"}
                            isBurnTimeChart={true}
                          />
                        </CustomSpinnerComponent>
                      </>
                    ) : null}
                  </>
                ),
                id: "Sensor",
                title: "Burn Time vs Sensor",
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default BurnTimeChart;
